import { Fragment, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '../lib/api'
import { Card, CardContent } from '../components/ui/Card'
import { Badge } from '../components/ui/Badge'

type Row = {
  id: string; tenantId: string | null; userEmail: string | null; userRole: string | null
  method: string; route: string; path: string; status: number; summary: unknown; createdAt: string
}
type Client = { id: string; name: string }

// Ações mais comuns → rótulo legível (chave = "MÉTODO rota"). Fallback = a rota.
const ACTIONS: Record<string, string> = {
  'POST /outbound': 'Criou ordem manual',
  'POST /outbound/:id/ship': 'Expediu pedido',
  'POST /outbound/:id/reopen': 'Reabriu pedido',
  'PATCH /outbound/:id/status': 'Alterou status do pedido',
  'POST /outbound/:id/label': 'Anexou etiqueta (PDF)',
  'POST /outbound/:id/label-zpl': 'Anexou etiqueta ZPL',
  'POST /outbound/client-shipment': 'Registrou envio do cliente',
  'POST /outbound/purge-no-nf': 'Removeu pedidos sem NF',
  'POST /expedition/ship-orders': 'Marcou pedidos enviados',
  'POST /expedition/ship-nfs': 'Marcou NFs processadas',
  'POST /separations/from-orders': 'Criou onda (pedidos)',
  'POST /separations/from-channel': 'Criou onda do canal',
  'POST /separations': 'Criou lote por arquivo',
  'POST /separations/:id/baixa': 'Concluiu separação (baixa de estoque)',
  'POST /separations/:id/abastecer': 'Abasteceu separação',
  'POST /separations/:id/cancel': 'Cancelou lote',
  'POST /separations/:id/release': 'Liberou lote',
  'POST /stock/adjust': 'Ajustou estoque',
  'POST /stock/outbound-batch': 'Baixa por separação',
  'POST /stock/count': 'Aplicou contagem (D0)',
  'POST /inbound': 'Criou recebimento',
  'PATCH /inbound/:id/status': 'Alterou recebimento',
  'POST /products': 'Criou produto',
  'PATCH /products/:id': 'Editou produto',
  'DELETE /products/:id': 'Excluiu produto',
  'POST /users': 'Criou usuário',
  'PATCH /users/:id': 'Editou usuário',
  'DELETE /users/:id': 'Excluiu usuário',
  'POST /clients': 'Criou cliente',
  'PATCH /clients/:id': 'Editou cliente',
  'POST /transfers': 'Criou transferência',
  'POST /pallets': 'Criou palete',
  'POST /integrations/refresh': 'Carregou pedidos (ERP)',
  'POST /integrations/nf-sync': 'Sincronizou por NF',
  'POST /integrations/ignored-recipients/cleanup': 'Limpou destinatários ignorados',
  'POST /integrations/nfkey-unique/resolve-duplicates': 'Resolveu duplicatas de NF',
}
const methodColor: Record<string, string> = {
  POST: 'bg-emerald-100 text-emerald-700', PATCH: 'bg-amber-100 text-amber-700',
  PUT: 'bg-amber-100 text-amber-700', DELETE: 'bg-red-100 text-red-700',
}

export function AuditPage() {
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [userEmail, setUserEmail] = useState('')
  const [q, setQ] = useState('')
  const [page, setPage] = useState(1)
  const [openId, setOpenId] = useState<string | null>(null)

  const { data: clients = [] } = useQuery<Client[]>({
    queryKey: ['clients'],
    queryFn: async () => (await api.get('/clients')).data,
  })
  const clientName = (id: string | null) => id ? (clients.find(c => c.id === id)?.name ?? id.slice(0, 8)) : '—'

  const { data: users = [] } = useQuery<string[]>({
    queryKey: ['audit-users'],
    queryFn: async () => (await api.get('/audit/users')).data,
  })

  const { data, isLoading } = useQuery<{ rows: Row[]; total: number; page: number; pageSize: number }>({
    queryKey: ['audit', from, to, userEmail, q, page],
    queryFn: async () => (await api.get('/audit', { params: {
      from: from || undefined, to: to || undefined, userEmail: userEmail || undefined, q: q || undefined, page,
    } })).data,
  })
  const rows = data?.rows ?? []
  const total = data?.total ?? 0
  const pageCount = Math.max(1, Math.ceil(total / (data?.pageSize ?? 100)))
  const action = (r: Row) => ACTIONS[`${r.method} ${r.route}`] ?? r.route

  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-xl font-semibold text-gray-900">Logs de alteração</h2>
        <p className="text-sm text-gray-500">Toda ação que altera dados fica registrada — quem, o quê e quando.</p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">De</label>
          <input type="date" value={from} onChange={e => { setFrom(e.target.value); setPage(1) }}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Até</label>
          <input type="date" value={to} onChange={e => { setTo(e.target.value); setPage(1) }}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm" />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-400">Usuário</label>
          <select value={userEmail} onChange={e => { setUserEmail(e.target.value); setPage(1) }}
            className="h-9 rounded-lg border border-gray-300 px-3 text-sm">
            <option value="">Todos</option>
            {users.map(u => <option key={u} value={u}>{u}</option>)}
          </select>
        </div>
        <input value={q} onChange={e => { setQ(e.target.value); setPage(1) }} placeholder="Buscar rota / e-mail…"
          className="h-9 min-w-56 flex-1 rounded-lg border border-gray-300 px-3 text-sm" />
        <span className="ml-auto self-center text-xs text-gray-400">{total} registro(s)</span>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Carregando…</p>
          ) : rows.length === 0 ? (
            <p className="px-5 py-10 text-center text-sm text-gray-400">Nenhum registro com esses filtros.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-gray-100 bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium">Quando</th>
                    <th className="px-4 py-3 text-left font-medium">Usuário</th>
                    <th className="px-4 py-3 text-left font-medium">Ação</th>
                    <th className="px-4 py-3 text-left font-medium">Cliente</th>
                    <th className="px-4 py-3 text-center font-medium">Status</th>
                    <th className="px-4 py-3" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => (
                    <Fragment key={r.id}>
                      <tr className="hover:bg-gray-50">
                        <td className="whitespace-nowrap px-4 py-2.5 text-gray-600">{new Date(r.createdAt).toLocaleString('pt-BR')}</td>
                        <td className="px-4 py-2.5">
                          <span className="text-gray-800">{r.userEmail ?? '—'}</span>
                          {r.userRole && <span className="ml-1 text-xs text-gray-400">({r.userRole})</span>}
                        </td>
                        <td className="px-4 py-2.5">
                          <span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-semibold ${methodColor[r.method] ?? 'bg-gray-100 text-gray-600'}`}>{r.method}</span>
                          <span className="text-gray-800">{action(r)}</span>
                        </td>
                        <td className="px-4 py-2.5 text-gray-600">{clientName(r.tenantId)}</td>
                        <td className="px-4 py-2.5 text-center">
                          <Badge variant={r.status < 300 ? 'success' : r.status < 500 ? 'warning' : 'danger'}>{r.status}</Badge>
                        </td>
                        <td className="px-4 py-2.5 text-right">
                          <button onClick={() => setOpenId(openId === r.id ? null : r.id)}
                            className="text-xs text-blue-600 hover:underline">{openId === r.id ? 'ocultar' : 'detalhes'}</button>
                        </td>
                      </tr>
                      {openId === r.id && (
                        <tr className="bg-gray-50/70">
                          <td colSpan={6} className="px-4 py-3">
                            <p className="mb-1 text-xs text-gray-500">Rota: <span className="font-mono">{r.path}</span></p>
                            <pre className="max-h-72 overflow-auto rounded-lg border border-gray-200 bg-white p-3 text-[11px] leading-tight text-gray-700">{JSON.stringify(r.summary, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>Página {page} de {pageCount}</span>
        <div className="flex gap-2">
          <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40">‹ anterior</button>
          <button disabled={page >= pageCount} onClick={() => setPage(p => p + 1)}
            className="rounded-lg border border-gray-300 px-3 py-1.5 disabled:opacity-40">próxima ›</button>
        </div>
      </div>
    </div>
  )
}
